export const OFFER_LETTER_BUCKET = 'offer-letters';
export const MAX_OFFER_PDF_BYTES = 10 * 1024 * 1024;
export const PDF_SIGNATURE = '%PDF-';
export const CANONICAL_FORKHIRE_OFFER_ORIGIN = 'https://hire.fork.sa';

type OfferIdentity = {
  org_id: string | null;
  application_id: string | null;
  pdf_storage_key?: string | null;
};

export type OfferPayloadSnapshot = {
  offer_type?: string | null;
  offer_title?: string | null;
  department?: string | null;
  work_location?: string | null;
  employment_type?: string | null;
  salary_amount?: number | string | null;
  salary_currency?: string | null;
  benefits_allowances?: string | null;
  probation_period?: string | null;
  expected_start_date?: string | null;
  notice_period?: string | null;
  employer_notes?: string | null;
  offer_expiry_days?: number | null;
  additional_email?: string | null;
  pdf_storage_key?: string | null;
  pdf_original_filename?: string | null;
};

export type ServerPdfObject = {
  bucketId: string;
  isBucketPrivate: boolean;
  path: string;
  orgId: string;
  applicationId: string;
  size: number;
  contentType: string;
  signature: string;
};

export type TrustedOfferOriginConfig = {
  origin?: string | null;
  allowlist?: string[];
};

export type UploadedPdfCleanupDecision = {
  uploadedPath: string | null | undefined;
  existingOfferPdfStorageKey?: string | null;
  linkedOfferPdfStorageKey?: string | null;
};

const OFFER_PAYLOAD_FIELDS: (keyof OfferPayloadSnapshot)[] = [
  'offer_type',
  'offer_title',
  'department',
  'work_location',
  'employment_type',
  'salary_amount',
  'salary_currency',
  'benefits_allowances',
  'probation_period',
  'expected_start_date',
  'notice_period',
  'employer_notes',
  'offer_expiry_days',
  'additional_email',
  'pdf_storage_key',
  'pdf_original_filename',
];

export function isGovernedOfferPdfStorageKey(path: string | null | undefined, orgId: string | null | undefined, applicationId: string | null | undefined) {
  if (!path || !orgId || !applicationId) return false;
  return path.startsWith(`${orgId}/${applicationId}/`) && !path.startsWith('/') && !path.includes('..');
}

export function shouldRenderOfferAsUploadedPdf(offer: OfferIdentity) {
  return isGovernedOfferPdfStorageKey(offer.pdf_storage_key, offer.org_id, offer.application_id);
}

export function shouldValidateOfferPayloadUpdate(previous: OfferPayloadSnapshot, next: OfferPayloadSnapshot) {
  return OFFER_PAYLOAD_FIELDS.some((field) => previous[field] !== next[field]);
}

export function verifyServerPdfObject(object: ServerPdfObject): { ok: true } | { ok: false; reason: string } {
  if (object.bucketId !== OFFER_LETTER_BUCKET) return { ok: false, reason: 'invalid_bucket' };
  if (!object.isBucketPrivate) return { ok: false, reason: 'bucket_must_be_private' };
  if (!isGovernedOfferPdfStorageKey(object.path, object.orgId, object.applicationId)) return { ok: false, reason: 'invalid_attachment_scope' };
  if (object.size <= 0 || object.size > MAX_OFFER_PDF_BYTES) return { ok: false, reason: 'invalid_size' };
  if (object.contentType !== 'application/pdf') return { ok: false, reason: 'invalid_content_type' };
  if (object.signature !== PDF_SIGNATURE) return { ok: false, reason: 'invalid_pdf_signature' };
  return { ok: true };
}

function normalizeTrustedOrigin(value: string) {
  const candidate = value.trim();
  if (!candidate || candidate.includes('*') || /\s/.test(candidate) || !/^https:\/\/[^/?#]+\/?$/i.test(candidate)) {
    throw new Error('invalid_origin_protocol');
  }

  const parsed = new URL(candidate);
  if (
    parsed.protocol !== 'https:' ||
    parsed.username ||
    parsed.password ||
    parsed.pathname !== '/' ||
    parsed.search ||
    parsed.hash ||
    parsed.hostname.toLowerCase().startsWith('hire.fork.sa.')
  ) {
    throw new Error('invalid_origin_protocol');
  }
  return parsed.origin;
}

export function resolveTrustedOfferOrigin(config: TrustedOfferOriginConfig) {
  const origin = config.origin ? normalizeTrustedOrigin(config.origin) : CANONICAL_FORKHIRE_OFFER_ORIGIN;
  const allowedOrigins = new Set([CANONICAL_FORKHIRE_OFFER_ORIGIN]);
  for (const allowed of config.allowlist ?? []) {
    allowedOrigins.add(normalizeTrustedOrigin(allowed));
  }
  if (!allowedOrigins.has(origin)) throw new Error('origin_not_allowlisted');
  return origin;
}

export function buildTrustedOfferLink(config: TrustedOfferOriginConfig, token: string) {
  return `${resolveTrustedOfferOrigin(config)}/offer/${token}`;
}

export function shouldRemoveUploadedPdfAfterFailure(decision: UploadedPdfCleanupDecision) {
  if (!decision.uploadedPath) return false;
  if (decision.existingOfferPdfStorageKey === decision.uploadedPath) return false;
  if (decision.linkedOfferPdfStorageKey === decision.uploadedPath) return false;
  return true;
}
